import type {
  Expense,
  ExpenseListResponse,
  CreateExpensePayload,
  ApprovalListResponse,
  ApprovalType,
} from '@/types/expense';
import { apiBlob } from '@/lib/api-client';

const BASE = '/api/v1/expenses';
const APPROVALS_BASE = '/api/v1/approvals';

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

async function request<T>(url: string, options?: RequestInit & { unwrap?: boolean }): Promise<T> {
  const { unwrap = true, ...fetchOptions } = options ?? {};
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...fetchOptions?.headers },
    ...fetchOptions,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message ?? 'Request failed');
  }
  const json = await res.json();
  return unwrap ? (json.data ?? json) : json;
}

export const expenseService = {
  list(params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    location_id?: string;
    from?: string;
    to?: string;
    /** T27 search facets. */
    description?: string;
    reference?: string;
  }): Promise<ExpenseListResponse> {
    const { from, to, ...rest } = params ?? {};
    return request<ExpenseListResponse>(
      `${BASE}${buildQuery({ page: 1, limit: 20, ...rest, start_date: from, end_date: to })}`,
      { unwrap: false },
    );
  },

  /** Every expense the list filters match as CSV — written by the API from all pages (T27). */
  exportCsv(params: {
    status?: string;
    category?: string;
    location_id?: string;
    from?: string;
    to?: string;
    description?: string;
    reference?: string;
  }) {
    const { from, to, ...rest } = params;
    return apiBlob(`/expenses/export${buildQuery({ ...rest, start_date: from, end_date: to })}`);
  },

  checkDuplicate(params: {
    location_id: string;
    category: string;
    amount: number;
  }): Promise<{
    duplicate: {
      id: string;
      amount: number;
      category: string;
      location_id: string;
      created_at: string;
    } | null;
  }> {
    return request(`${BASE}/duplicate-check${buildQuery(params)}`);
  },

  getById(id: string): Promise<Expense> {
    return request<Expense>(`${BASE}/${id}`);
  },

  create(payload: CreateExpensePayload): Promise<Expense> {
    return request<Expense>(BASE, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  approve(id: string, justification?: string): Promise<Expense> {
    return request<Expense>(`${BASE}/${id}/approve`, {
      method: 'PATCH',
      body: JSON.stringify({ justification }),
    });
  },

  reject(id: string, reason: string): Promise<Expense> {
    return request<Expense>(`${BASE}/${id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    });
  },

  hold(id: string, holdReason?: string): Promise<Expense> {
    return request<Expense>(`${BASE}/${id}/hold`, {
      method: 'PATCH',
      body: JSON.stringify({ hold_reason: holdReason }),
    });
  },
};

export const approvalService = {
  list(params?: {
    status?: string;
    type?: ApprovalType | 'all';
    page?: number;
    limit?: number;
  }): Promise<ApprovalListResponse> {
    const { type, ...rest } = params ?? {};
    return request<ApprovalListResponse>(
      `${APPROVALS_BASE}${buildQuery({ status: 'PENDING', ...rest, ...(type && type !== 'all' ? { type } : {}) })}`,
      { unwrap: false },
    );
  },

  approve(id: string, reviewNote?: string): Promise<void> {
    return request<void>(`${APPROVALS_BASE}/${id}/approve`, {
      method: 'PATCH',
      body: JSON.stringify(reviewNote ? { review_note: reviewNote } : {}),
    });
  },

  reject(id: string, reason: string): Promise<void> {
    return request<void>(`${APPROVALS_BASE}/${id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ review_note: reason }),
    });
  },
};
